<template>
  <article class="listing-map-card__item" @click="onClick">
    <div class="listing-map-card__media" :class="{ 'listing-map-card__media--loading': loadingMedia }">
      <img v-if="image" class="listing-map-card__image" :src="image" alt="" loading="lazy" />
      <div v-else-if="!loadingMedia" class="listing-map-card__image listing-map-card__image--empty">
        <SvgIcon name="home" :size="28" />
      </div>
    </div>

    <div class="listing-map-card__body">
      <template v-if="item">
        <!-- The chip from ListingCard; in the body for the same reason as on
             the projects card (compact layout on narrow maps) -->
        <div v-if="item.status || phaseLabel" class="listing-map-card__chips">
          <span v-if="item.status" class="listing-card__status" :class="statusModifier">
            <span class="listing-card__status-dot" />
            {{ item.status }}
          </span>
          <span v-if="phaseLabel" class="listing-card__status" :data-status="phaseLabel">
            <span class="listing-card__status-dot" />
            {{ phaseLabel }}
          </span>
        </div>
        <h3 class="listing-map-card__title">{{ title }}</h3>
        <p v-if="locationLabel" class="listing-map-card__location">
          <SvgIcon name="pin" :size="14" />
          <span>{{ locationLabel }}</span>
        </p>
        <p class="listing-map-card__price">
          <span v-if="price.prefix" class="listing-map-card__price-note">{{ price.prefix }}</span>
          <strong>{{ price.value }}</strong>
          <span v-if="price.suffix" class="listing-map-card__price-note">{{ price.suffix }}</span>
        </p>
      </template>
      <p v-else-if="error" class="listing-map-card__state">{{ t('listing-map.popup.error') }}</p>
      <div v-else class="listing-map-card__skeleton" aria-hidden="true">
        <span class="listing-card-skeleton__line listing-card-skeleton__line--title" />
        <span class="listing-card-skeleton__line listing-card-skeleton__line--location" />
        <span class="listing-card-skeleton__line" />
      </div>
    </div>
  </article>
</template>

<script setup>
import { computed } from 'vue';
import SvgIcon from './SvgIcon.vue';
import { housingTitleWithNumber } from '../lib/housingTitle';
import { t } from '../lib/i18n';
import { listingPrice, listingStatusModifier, registrationPhaseLabel } from '../lib/listingCardFields';
import { firstImageUrl } from '../lib/mapCardImage';

// One home (construction number) in the listing map card. The marker endpoint
// already gives title, place, price and status, so those render at once; the
// photo follows from the home's detail payload.
const props = defineProps({
  item: { type: Object, default: null },
  listing: { type: Object, default: null },
  listingError: { type: Boolean, default: false },
  error: { type: Boolean, default: false }
});

const emit = defineEmits(['select']);

const onClick = () => {
  if (props.item) emit('select', props.item);
};

const loadingMedia = computed(() => Boolean(props.item) && !props.listing && !props.listingError);

const image = computed(() => firstImageUrl(props.listing?.images));

const title = computed(() => housingTitleWithNumber(props.item));

const locationLabel = computed(() => {
  const main = [props.item.city, props.item.project].filter(Boolean).join(', ');
  return props.item.subProject ? `${main} – ${props.item.subProject}` : main;
});

const price = computed(() => listingPrice(props.item));

const statusModifier = computed(() => listingStatusModifier(props.item.status));

const phaseLabel = computed(() => registrationPhaseLabel(props.item.subProjectStatus));
</script>
